window.NPLP = window.NPLP || {};
window.NPLP.LPProductFormHandler = class LPProductFormHandler {

/**
 * Initializes the LPProductFormHandler.
 *
 * Required CSS classes and locations:
 * - .lpfp-pack: Applied to each product pack/variant container
 * - .lpfp-totals: Container for total price information
 * - .lpfp-total-price: Element displaying the total price
 * - .lpfp-total-percent-off: Element displaying the total percentage off
 * - .lpfp-total-compare-at-price: Element displaying the total compare-at price
 * - .lpfp-total-saved-price: Element displaying the total saved price
 * - .lpfp-total-subscription-price: Element displaying the subscription price
 * - .lpfp-submit: Submit button for the form
 * - .lpfp-price-per-pack: Element displaying the price per pack (inside .lpfp-pack)
 * - .lpfp-price: Element displaying the pack price (inside .lpfp-pack)
 * - .lpfp-compare-at-price: Element displaying the pack compare-at price (inside .lpfp-pack)
 * - .lpfp-saved: Element displaying the pack saved amount (inside .lpfp-pack)
 * - .lpfp-percentage-off: Element displaying the pack percentage off (inside .lpfp-pack)
 *
 * Optional CSS classes:
 * - .lpfp--has-subscription: Added to .lpfp-totals when subscriptions are available
 * - .lpfp--is-discounted: Added to .lpfp-totals and .lpfp-pack when there's a discount
 * - .lpfp--no-discount--hide: Elements with this class are hidden when there's no discount
 * - .hidden: Used to hide elements
 *
 * Example HTML structure:
 * <div id="lp-product-form">
 *   <ul class="lpfp-pack-list">
 *     <li class="lpfp-pack"
 *         data-id="SKU123"
 *         data-name="Pack 1"
 *         data-price="19.99"
 *         data-compare-at-price="24.99"
 *         data-selling-plan-id="789"
 *         data-subscription-percent-off="10"
 *         data-pack-count="3">
 *       <span class="lpfp-price-per-pack"></span>
 *       <span class="lpfp-price"></span>
 *       <span class="lpfp-compare-at-price"></span>
 *       <span class="lpfp-saved"></span>
 *       <span class="lpfp-percentage-off"></span>
 *     </li>
 *     <!-- More .lpfp-pack elements as list items -->
 *   </ul>
 *   <div class="lpfp-totals">
 *     <span class="lpfp-total-price"></span>
 *     <span class="lpfp-total-percent-off"></span>
 *     <span class="lpfp-total-compare-at-price"></span>
 *     <span class="lpfp-total-saved-price"></span>
 *     <span class="lpfp-total-subscription-price"></span>
 *   </div>
 *   <button class="lpfp-submit">Add to Cart</button>
 * </div>
 *
 * @param {Object} options - Configuration options for the form handler.
 * @param {string} [options.instanceName="LPProductFormHandler"] - Name of this instance.
 * @param {boolean} [options.debug=false] - Enable debug logging.
 * @param {string|jQuery} [options.container="#lp-product-form"] - Selector or jQuery object for the form container.
 * @param {string} [options.activeVariantClassOverride="lpfp-selected-variant"] - Class name for the active variant.
 */
  constructor(options = {}) {
    this.instanceName = options.instanceName || "LPProductFormHandler";
    this.debug = options.debug || false;
    this.container = $(options.container) || $("#lp-product-form");
    this.log = this.createLogger();
    this.packsEls = this.container.find(".lpfp-pack");

    this.totalsEl = this.container.find(".lpfp-totals");
    this.totalPriceEl = this.container.find(".lpfp-total-price");
    this.totalPercentOffEl = this.container.find(".lpfp-total-percent-off");
    this.totalCompareAtPriceEl = this.container.find(
      ".lpfp-total-compare-at-price"
    );
    this.totalSavedPriceEl = this.container.find(".lpfp-total-saved-price");

    this.subscriptionPriceEl = this.container.find(
      ".lpfp-total-subscription-price"
    );
    this.subscriptionsEnabled = false;

    this.submitEl = this.container.find(".lpfp-submit");
    this.activeVariantClassOverride =
      options.activeVariantClassOverride || "lpfp-selected-variant";
    this.perPackFormat = options.perPackFormat || "%s Each";

    if (window.LPProductForm?.isInitialized) {
      this.initFormBindings();
    } else {
      const checkInterval = setInterval(() => {
        if (window.LPProductForm?.isInitialized) {
          clearInterval(checkInterval);
          this.initFormBindings();
        }
      }, 100);
    }
  }

  createLogger() {
    const colors = {
      info: "#3498db",
      success: "#2ecc71",
      error: "#e74c3c",
    };

    const logFn = (type, ...args) => {
      if (this.debug) {
        console.log(
          `%cNP-${this.instanceName}`,
          `background: ${colors[type]}; color: white; padding: 2px 5px; border-radius: 3px;`,
          ...args
        );
      }
    };

    return {
      info: (...args) => logFn("info", ...args),
      success: (...args) => logFn("success", ...args),
      error: (...args) => logFn("error", ...args),
    };
  }

  updateTotals() {
    this.log.info("updateTotals()");
    const totals = window.LPProductForm.getTotal();
    const formatted = totals.formatted;

    this.totalPriceEl.text(formatted.price);
    this.totalPercentOffEl.text(formatted.percentageOff);
    this.totalCompareAtPriceEl.text(formatted.compareAtPrice);
    this.totalSavedPriceEl.text(formatted.saved);

    if (formatted.hasSubscriptions === true) {
      this.log.info("hasSubscriptions");
      this.totalsEl.addClass("lpfp--has-subscription");
      this.subscriptionPriceEl.text(formatted.subscriptionPrice);
    } else {
      this.log.info("hasSubscriptions is false");
      this.totalsEl.removeClass("lpfp--has-subscription");
    }

    const isDiscounted = totals.total.saved > 0; 
    this.log.info(`isDiscounted: ${isDiscounted}`);
    this.totalsEl.toggleClass('lpfp--is-discounted', isDiscounted);
    this.totalsEl.find('.lpfp--no-discount--hide').toggleClass('hidden', !isDiscounted);
  }

  restrictToSubscriptionOnly() {
    this.log.info("restrictToSubscriptionOnly()");
    this.subscriptionsEnabled = true;
    this.updateVariants();
    this.updateForm();
    this.updateTotals();
  }

  getOrCreatePackInfo(pack) {
    let info = pack.data('packInfo');
    if (!info) {
      info = {
      sku: pack.attr("data-id"),
      quantity: 1,
      name: pack.attr("data-name"),
      price: parseFloat(pack.attr("data-price").replace(/[^\d.-]/g, "")) * 100,
      compareAtPrice: parseFloat((pack.attr("data-compare-at-price") || pack.attr('data-price')).replace(/[^\d.-]/g, "")) * 100,
      sellingPlanId: pack.attr("data-selling-plan-id"),
      sellingPlanPercentOff: pack.attr("data-subscription-percent-off") || 0,
      packCount: pack.attr("data-pack-count") || 1,
    };
      pack.data('packInfo', info);
    }
    return info;
  }

  updateVariants(hookBefore = () => {}, hookAfter = () => {}) {
    hookBefore();
    this.packsEls.each((_, element) => {
      const pack = $(element);
      const info = this.getOrCreatePackInfo(pack);
      const { raw, formatted } = window.LPProductForm.getVariantTotals(info);
      this.log.info(`Updating variant ${info.name}`, info, raw, formatted);
      
      if (formatted.pricePerPack) {
        pack.find(".lpfp-price-per-pack")?.text(this.perPackFormat.replace("%s", formatted.pricePerPack));
      }
      pack.find(".lpfp-price")?.text(formatted.price);
      pack.find(".lpfp-compare-at-price")?.text(formatted.compareAtPrice);
      pack.find(".lpfp-saved")?.text(formatted.saved);
      pack.find(".lpfp-percentage-off")?.text(formatted.percentageOff);

      const isDiscounted = raw.saved > 0;
      pack.toggleClass('lpfp--is-discounted', isDiscounted);
      pack.find('.lpfp--no-discount--hide').toggleClass('hidden', !isDiscounted);
    });
    hookAfter();
  }

  updateForm(hookBefore = () => {}, hookAfter = () => {}) {
    hookBefore();

    const selectedPack = this.container.find(
      `.lpfp-pack.${this.activeVariantClassOverride}`
    );
    if (selectedPack.length) {
      const info = this.getOrCreatePackInfo(selectedPack);
      this.log.info(`updateForm() - Option selected: ${info.name}`);

      window.LPProductForm.setSKUs([
        info,
      ]);
    }

    hookAfter();
  }

  initFormBindings() {
    this.log.info("initFormBindings()");
    this.updateVariants();
    this.updateForm();
    this.updateTotals();

    this.packsEls.on("click", (e) => {
      this.log.info(".pack clicked()");
      $(e.currentTarget)
        .addClass(this.activeVariantClassOverride)
        .siblings()
        .removeClass(this.activeVariantClassOverride);
      this.updateForm();
      this.updateTotals();
    });

    this.submitEl.click(async (event) => {
      event.preventDefault();
      this.log.info("Form submission intercepted");
      await window.LPProductForm.submitCart();
    });

    window.addEventListener("locGateSubsAdded", () => {
      this.restrictToSubscriptionOnly();
    });

    if (window.locGated === true) {
      this.restrictToSubscriptionOnly();
    }

    this.log.success("initFormBindings() - Form initialization complete");
  }
};
